import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { src, srcSet, isOffsite } from "../../lib/img";

export const Photo = ({ img, sizes = "100vw", className = "", eager = false, style, ...rest }) => (
  <img
    src={src(img.p, img.w >= 1500 ? 1500 : 1000)}
    srcSet={srcSet(img.p, img.w)}
    sizes={sizes}
    alt={img.alt}
    loading={eager ? "eager" : "lazy"}
    fetchPriority={eager ? "high" : "auto"}
    decoding="async"
    className={className}
    style={{ objectPosition: img.pos, ...style }}
    {...rest}
  />
);

export const Eyebrow = ({ children, className = "", tone = "light" }) => (
  <p className={`eyebrow text-[11px] font-semibold uppercase tracking-[0.28em] ${tone === "dark" ? "text-coral-soft" : "text-coral-ink"} ${className}`}>
    {children}
  </p>
);

const VARIANTS = {
  ink: "bg-ink text-ivory hover:bg-ocean-deep",
  coral: "bg-coral-ink text-ivory hover:bg-[#8f3f22]",
  ivory: "bg-ivory text-ink hover:bg-white",
  outline: "border border-ink/25 text-ink hover:border-ink hover:bg-ink hover:text-ivory",
  outlineLight: "border border-ivory/50 text-ivory hover:bg-ivory hover:text-ink",
};

export const SmartLink = ({ href, children, ...rest }) => {
  if (href.startsWith("/")) return <Link to={href} {...rest}>{children}</Link>;
  const off = isOffsite(href);
  return (
    <a href={href} target={off ? "_blank" : undefined} rel={off ? "noopener noreferrer" : undefined} {...rest}>
      {children}
    </a>
  );
};

export const CTA = ({ href, children, variant = "ink", testid, className = "", onClick, icon = true }) => {
  const off = isOffsite(href);
  return (
    <SmartLink
      href={href}
      onClick={onClick}
      data-testid={testid}
      className={`group inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold tracking-wide transition-[background-color,color,border-color,transform] duration-300 active:scale-[0.98] ${VARIANTS[variant]} ${className}`}
    >
      {children}
      {icon && <ArrowUpRight aria-hidden className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />}
      {off && <span className="sr-only">(opens in a new tab)</span>}
    </SmartLink>
  );
};

export const TextLink = ({ href, children, testid, light = false, className = "" }) => {
  const off = isOffsite(href);
  return (
    <SmartLink
      href={href}
      data-testid={testid}
      className={`group inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold ${light ? "text-ivory" : "text-ink"} ${className}`}
    >
      <span className={`bg-[length:100%_1px] bg-left-bottom bg-no-repeat pb-0.5 transition-[background-size] duration-500 group-hover:bg-[length:0%_1px] ${light ? "bg-[linear-gradient(currentColor,currentColor)]" : "bg-[linear-gradient(#A84E2C,#A84E2C)]"}`}>
        {children}
      </span>
      <ArrowUpRight aria-hidden className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      {off && <span className="sr-only">(opens in a new tab)</span>}
    </SmartLink>
  );
};

export const Reveal = ({ children, delay = 0, y = 28, className = "", as = "div" }) => {
  const reduce = useReducedMotion();
  const Tag = as;
  if (reduce) return <Tag className={className}>{children}</Tag>;
  const M = motion[as];
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -12% 0px" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </M>
  );
};

const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.16, 1]);
  return (
    <motion.span aria-hidden style={{ opacity }} className="inline-block pr-[0.24em]">
      {children}
    </motion.span>
  );
};

export const WordReveal = ({ text, className = "", testid }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.88", "start 0.3"] });
  const words = text.split(" ");
  return (
    <p ref={ref} className={className} data-testid={testid} aria-label={reduce ? undefined : text}>
      {reduce
        ? text
        : words.map((w, i) => (
            <Word key={i} progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]}>
              {w}
            </Word>
          ))}
    </p>
  );
};

export const useParallax = (ref, range = ["-8%", "8%"]) => {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  return useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : range);
};
